import { useCallback, useEffect, useState } from "react";

export type CartLine = {
  menu_item_id: string;
  product_id: string;
  name: string;
  price_minor: number;
  qty: number;
  /** Set when this line came from an accepted pairing suggestion. */
  upsell_rule_id?: string | null;
};

type CartState = { eventSlug: string; lines: CartLine[]; idempotencyKey: string };

const key = (slug: string) => `prispa.cart.${slug}`;

/**
 * The cart lives in localStorage, so it survives a page reload and a dropped
 * connection — which in Pohrebea is not an edge case.
 *
 * The idempotency key is minted ONCE per cart, not per tap. That is what makes
 * a double tap on "pay" resolve to one order instead of two.
 */
function read(slug: string): CartState {
  try {
    const raw = localStorage.getItem(key(slug));
    if (raw) return JSON.parse(raw) as CartState;
  } catch {
    /* corrupt cart is not worth crashing the menu over */
  }
  return { eventSlug: slug, lines: [], idempotencyKey: crypto.randomUUID() };
}

function write(state: CartState): void {
  try {
    localStorage.setItem(key(state.eventSlug), JSON.stringify(state));
  } catch {
    /* quota */
  }
}

export function useCart(eventSlug: string) {
  const [state, setState] = useState<CartState>(() => read(eventSlug));

  useEffect(() => {
    write(state);
  }, [state]);

  const add = useCallback((line: Omit<CartLine, "qty">, qty = 1) => {
    setState((s) => {
      const existing = s.lines.find((l) => l.menu_item_id === line.menu_item_id);
      const lines = existing
        ? s.lines.map((l) => (l.menu_item_id === line.menu_item_id ? { ...l, qty: l.qty + qty } : l))
        : [...s.lines, { ...line, qty }];
      return { ...s, lines };
    });
  }, []);

  const setQty = useCallback((menuItemId: string, qty: number) => {
    setState((s) => ({
      ...s,
      lines: qty <= 0
        ? s.lines.filter((l) => l.menu_item_id !== menuItemId)
        : s.lines.map((l) => (l.menu_item_id === menuItemId ? { ...l, qty } : l)),
    }));
  }, []);

  /** After a successful order the next cart must get a NEW idempotency key. */
  const clear = useCallback(() => {
    setState({ eventSlug, lines: [], idempotencyKey: crypto.randomUUID() });
  }, [eventSlug]);

  /**
   * THE BASKET SPEAKS THE MENU'S LANGUAGE.
   *
   * A line's name is copied at `add` — in whatever language the guest was
   * reading THEN — and the checkout went on showing it after they switched:
   * water added under Romanian sat in a Russian basket as „Apă plată" beside
   * beers added later in Russian. The menu calls this with every snapshot it
   * renders (each language is its own snapshot, so the names arrive already
   * translated), and the stored lines follow the reader.
   *
   * Returns the SAME state object when nothing changed, so the effect that
   * calls it on every snapshot cannot start a render loop.
   */
  const relabel = useCallback((names: ReadonlyMap<string, string>) => {
    setState((s) => {
      let changed = false;
      const lines = s.lines.map((l) => {
        const name = names.get(l.menu_item_id);
        if (name && name !== l.name) {
          changed = true;
          return { ...l, name };
        }
        return l;
      });
      return changed ? { ...s, lines } : s;
    });
  }, []);

  const qtyOf = useCallback(
    (menuItemId: string) => state.lines.find((l) => l.menu_item_id === menuItemId)?.qty ?? 0,
    [state.lines]
  );

  const count = state.lines.reduce((n, l) => n + l.qty, 0);
  const subtotal = state.lines.reduce((n, l) => n + l.price_minor * l.qty, 0);

  return { lines: state.lines, idempotencyKey: state.idempotencyKey, add, setQty, qtyOf, clear, relabel, count, subtotal };
}
